import React, { useState, useEffect } from 'react';
import styles from '../recommendations-widget/index.module.scss';

/**
 * SurfaceRecs — the context/deterministic recommendation surfaces of the <cdp-recs>
 * element, complementing the flagship personalized RecommendationsWidget:
 *
 *   complementary    "Goes with this"   -> /recommendations/for-model/{model}/complementary
 *   recently-viewed  "Continue"         -> /recommendations/recently-viewed/{userId}  (consent-gated)
 *   popular          "Popular"          -> /recommendations/popular
 *   related          "Similar to this"  -> /recommendations/for-user/{id}?related=1
 *   next-best-action "What to do next"  -> /recommendations/next-best-action  (renders CTA chips)
 *
 * One-shot fetch per surface (no personalize-upgrade dance); identity/consent come from the
 * same neutral window hooks the personalized widget uses. Renders into light DOM and reuses
 * the recommendations-widget card styles for visual consistency.
 */

export type SurfaceType =
  | 'complementary'
  | 'recently-viewed'
  | 'popular'
  | 'related'
  | 'next-best-action';

interface Item {
  id: string;
  title?: string;
  reason?: string;
  image?: string;
  url?: string;
  type?: string;
}

interface SurfaceRecsProps {
  surface: SurfaceType;
  heading?: string;
  count?: number;
  apiBase?: string;
  modelSlug?: string; // context model (complementary / related / popular-exclude)
  variant?: string; // variant slug for complementary
  stage?: string; // funnel stage for next-best-action
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = (typeof window !== 'undefined' ? window : {}) as any;

function hasConsent(): boolean {
  try {
    const fn = w.CdpRecsConsent || w.SubaruRecsConsent;
    if (typeof fn === 'function') return fn() === true;
  } catch {
    /* ignore */
  }
  try {
    if (w.ketchConsent && typeof w.ketchConsent.analytics !== 'undefined')
      return w.ketchConsent.analytics === true;
  } catch {
    /* ignore */
  }
  return false;
}

function cdpUserId(): string | null {
  return w.CdpUserId || w.SubaruCdpUserId || null;
}

function pushCDP(payload: unknown): void {
  try {
    if (typeof w.pushDigitalData === 'function') w.pushDigitalData(payload);
  } catch {
    /* analytics must never break the widget */
  }
}

const DEFAULT_HEADINGS: Record<SurfaceType, string> = {
  complementary: 'Goes with this',
  'recently-viewed': 'Continue where you left off',
  popular: 'Popular with shoppers',
  related: 'Similar models',
  'next-best-action': 'What would you like to do?',
};

export const SurfaceRecs = (props: SurfaceRecsProps): React.ReactElement => {
  const surface = props.surface;
  const heading = props.heading || DEFAULT_HEADINGS[surface] || 'Recommended';
  const count = props.count || 4;
  const apiBase = (
    props.apiBase ||
    w.RecommendationsApiBase ||
    'https://ai-api-staging.inchcape-web.au'
  ).replace(/\/$/, '');
  const model =
    props.modelSlug ||
    (typeof window !== 'undefined'
      ? window.location.pathname.split('/').filter(Boolean)[0] || ''
      : '');

  const [items, setItems] = useState<Item[]>([]);
  const [source, setSource] = useState<string>('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const n = `num=${encodeURIComponent(String(count))}`;
    const ctx = model ? `&context_item=${encodeURIComponent(model)}` : '';

    function buildUrl(): string | null {
      switch (surface) {
        case 'complementary': {
          if (!model) return null;
          const q = props.variant
            ? `?variant=${encodeURIComponent(props.variant)}&${n}`
            : `?${n}`;
          return `${apiBase}/recommendations/for-model/${encodeURIComponent(
            model
          )}/complementary${q}`;
        }
        case 'recently-viewed': {
          const uid = cdpUserId();
          if (!hasConsent() || !uid) return null; // personal history: needs consent + id
          return `${apiBase}/recommendations/recently-viewed/${encodeURIComponent(
            uid
          )}?consent=true&${n}${ctx}`;
        }
        case 'popular':
          return `${apiBase}/recommendations/popular?${n}${ctx}`;
        case 'related': {
          const uid = cdpUserId() || '__anonymous__';
          return `${apiBase}/recommendations/for-user/${encodeURIComponent(
            uid
          )}?related=1&consent=true&${n}${ctx}`;
        }
        case 'next-best-action': {
          const st = props.stage
            ? `&stage=${encodeURIComponent(props.stage)}`
            : '';
          const m = model ? `&model=${encodeURIComponent(model)}` : '';
          return `${apiBase}/recommendations/next-best-action?${n}${m}${st}`;
        }
        default:
          return null;
      }
    }

    function load(): void {
      const url = buildUrl();
      if (!url) {
        setReady(true);
        return;
      }
      fetch(url, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => {
          if (cancelled) return;
          setItems((data && data.items) || []);
          setSource((data && data.source) || '');
          setReady(true);
        })
        .catch(() => {
          if (!cancelled) setReady(true);
        });
    }

    load();
    // recently-viewed depends on async consent + fingerprint; retry when they resolve.
    const retry = () => load();
    if (surface === 'recently-viewed') {
      try {
        if (typeof w.ketch === 'function') w.ketch('on', 'consent', retry);
      } catch {
        /* ignore */
      }
      window.addEventListener('CdpUserIdReady', retry);
      window.addEventListener('SubaruCdpUserIdReady', retry);
    }
    return () => {
      cancelled = true;
      window.removeEventListener('CdpUserIdReady', retry);
      window.removeEventListener('SubaruCdpUserIdReady', retry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ready && items.length) {
      pushCDP({
        event: '_recommendationImpression',
        recommendation: {
          source: source || surface,
          surface,
          context: model || null,
          items: items.map((it) => it.id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, items, source]);

  if (!(ready && items.length)) {
    return <div className={styles.recommendations} />;
  }

  const onClick = (it: Item, i: number): void =>
    pushCDP({
      event: '_recommendationClick',
      recommendation: {
        id: it.id,
        position: i + 1,
        source: source || surface,
        surface,
        context: model || null,
      },
    });

  // Next-best-action renders CTA chips (links), not product cards.
  if (surface === 'next-best-action') {
    return (
      <div className={styles.recommendations}>
        <div className={styles.container}>
          <h2 className={styles.heading}>{heading}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {items.map((it, i) => (
              <a
                key={it.id || i}
                href={it.url || '#'}
                data-rec-id={it.id || ''}
                onClick={() => onClick(it, i)}
                style={{
                  display: 'inline-block',
                  padding: '12px 22px',
                  borderRadius: '6px',
                  background: '#1637A0',
                  color: '#fff',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                {it.title || it.id}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Card grid — complementary / recently-viewed / popular / related.
  return (
    <div className={styles.recommendations}>
      <div className={styles.container}>
        <h2 className={styles.heading}>{heading}</h2>
        <div className={styles.grid}>
          {items.map((it, i) => (
            <a
              key={it.id || i}
              className={styles.card}
              href={it.url || '#'}
              data-rec-id={it.id || ''}
              onClick={() => onClick(it, i)}
            >
              {it.image ? (
                <img
                  className={styles.img}
                  src={it.image}
                  alt={it.title || ''}
                  loading="lazy"
                />
              ) : (
                <div className={styles.img} />
              )}
              <div className={styles.body}>
                <p className={styles.title}>{it.title || it.id}</p>
                {it.reason ? (
                  <p className={styles.reason}>{it.reason}</p>
                ) : null}
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
